// ─────────────────────────────────────────────────────────────
// PajuStockService — 파주물류센터 재고 스냅샷 (paju_stock 테이블)
// v7.309
//
// ERP [전체매장 재고조회 … 자사재고] 파일의 D열(파주물류센터)을 담는다.
// 스냅샷이므로 업로드마다 전량 교체한다 (delete → insert).
//
// 스키마: migrations/v7.309-paju-stock.sql
//   product_name TEXT PRIMARY KEY / qty INTEGER / snapshot_date DATE / updated_at TIMESTAMPTZ
//
// ⚠ SQL 미실행 상태에서도 대시보드는 정상적으로 떠야 한다.
//   그래서 조회 실패는 던지지 않고 ok:false + 빈 맵으로 돌려준다.
// ─────────────────────────────────────────────────────────────
import { supabase } from '../supabase';

// 제품명 정규화 — 대소문자/공백/하이픈/언더스코어 차이를 흡수한다.
//   ERP 표기와 카탈로그 표기가 미세하게 다른 경우(TS-233 vs TS233)를 위한 2차 매칭용.
export const normalizeProductName = (name) =>
  String(name || '').toUpperCase().replace(/[\s\-_./]/g, '');

export const PajuStockService = {
  // 전체 조회 → { ok, map, normMap, snapshotDate, updatedAt, count, msg }
  //   map     : { 제품명 그대로: 수량 }
  //   normMap : { 정규화된 제품명: 수량 }  (1차 매칭 실패 시 폴백)
  async loadAll() {
    const empty = { map: {}, normMap: {}, snapshotDate: null, updatedAt: null, count: 0 };
    try {
      const { data, error } = await supabase
        .from('paju_stock')
        .select('product_name,qty,snapshot_date,updated_at');
      if (error) return { ok: false, msg: error.message, ...empty };
      const map = {}, normMap = {};
      let snapshotDate = null, updatedAt = null;
      (data || []).forEach(r => {
        const qty = Number(r.qty) || 0;
        map[r.product_name] = qty;
        const nk = normalizeProductName(r.product_name);
        // 정규화 충돌 시 큰 쪽을 남기지 않고 첫 값을 유지한다 (원본 우선이 자연스럽다)
        if (normMap[nk] === undefined) normMap[nk] = qty;
        if (r.snapshot_date && (!snapshotDate || r.snapshot_date > snapshotDate)) snapshotDate = r.snapshot_date;
        if (r.updated_at && (!updatedAt || r.updated_at > updatedAt)) updatedAt = r.updated_at;
      });
      return { ok: true, map, normMap, snapshotDate, updatedAt, count: (data || []).length };
    } catch (e) {
      return { ok: false, msg: e.message || String(e), ...empty };
    }
  },

  // 전량 교체. rows = [{ product_name, qty }]
  //   snapshotDate: 'YYYY-MM-DD' 또는 null
  async replaceAll(rows, snapshotDate) {
    try {
      // ① 기존 전체 삭제 — Supabase 는 무조건 filter 를 요구하므로 not-null 조건을 쓴다
      const del = await supabase.from('paju_stock').delete().not('product_name', 'is', null);
      if (del.error) return { ok: false, msg: del.error.message };

      const clean = (rows || [])
        .filter(r => r && r.product_name && String(r.product_name).trim())
        .map(r => ({
          product_name: String(r.product_name).trim(),
          qty: Number(r.qty) || 0,
          snapshot_date: snapshotDate || null,
          updated_at: new Date().toISOString(),
        }));
      if (clean.length === 0) return { ok: true, count: 0 };

      // ② 같은 제품명이 두 번 나오면 insert 가 PK 충돌로 통째로 실패한다 → 합산해서 하나로
      const merged = new Map();
      clean.forEach(r => {
        const prev = merged.get(r.product_name);
        if (prev) prev.qty += r.qty;
        else merged.set(r.product_name, { ...r });
      });
      const list = [...merged.values()];

      // ③ 청크 삽입 (요청 크기 제한 회피)
      const CHUNK = 500;
      for (let i = 0; i < list.length; i += CHUNK) {
        const { error } = await supabase.from('paju_stock').insert(list.slice(i, i + CHUNK));
        if (error) return { ok: false, msg: error.message, count: i };
      }
      return { ok: true, count: list.length };
    } catch (e) {
      return { ok: false, msg: e.message || String(e) };
    }
  },

  // 전체 삭제 (파주 표기 끄기)
  async clearAll() {
    try {
      const { error } = await supabase.from('paju_stock').delete().not('product_name', 'is', null);
      if (error) return { ok: false, msg: error.message };
      return { ok: true };
    } catch (e) {
      return { ok: false, msg: e.message || String(e) };
    }
  },
};
